import React, { useEffect, useState, useRef, useContext } from 'react';
import { Link, useNavigate, useParams } from "react-router-dom";
import images from '../../../../services/images';
import ContentHeader from '../../../parts/ContentHeader';
import Layout from '../../../parts/Layout';
import helpers from '../../../../services/helpers';
import { httpRequest, httpAuthHeaders } from '../../../../services/httpService';
import ProfileContext from '../../../../services/profileContext';
import Loader from '../../../parts/Loader';
import initTreeView from '../../../../services/initTreeView';
import ContactListModal from '../../../modals/ContactListModal';
import RenderErrors from '../../../elements/RenderErrors';
import VehiclesSearchModal from '../vehicles/VehiclesSearchModal';
import Swal from 'sweetalert2';
import { InputMask } from 'primereact/inputmask';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { Steps } from 'primereact/steps';
import { InputSwitch } from 'primereact/inputswitch';
import { AutoComplete } from 'primereact/autocomplete';
import { Menu } from 'primereact/menu';

import { Panel } from 'primereact/panel';
import { 
    FaMapMarkerAlt, 
    FaTruck, 
    FaExchangeAlt,
    FaSearch, 
    FaCalendarAlt, 
    FaBus, 
    FaCreditCard, 
    FaCheckCircle, 
    FaEdit,
    FaChevronLeft, 
    FaChevronRight,
    FaShip, 
    FaCheck, 
    FaReceipt,
    FaPrint,
    FaTimesCircle,
    FaAngleDown,
    FaAnchor
} from "react-icons/fa";
import { FaFileCircleCheck } from "react-icons/fa6";
import { CiReceipt } from "react-icons/ci";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RiCalendarScheduleFill } from "react-icons/ri";

export default function BookingViewForm ({viewer = 'admin', menu = 'manage-bookings', page_title = "Booking"}){
    const {
        account
    } = useContext(ProfileContext);
    const { booking_id } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);
    const menuRight = useRef(null);
    const today = new Date();
    const [loading, setLoading] = useState(0);
    const [errors, setErrors] = useState([]);
    const [bookingDetails, setBookingDetails] = useState([]);
    const [menuAlignment, setMenuAlignment] = useState('right'); // Default desktop to right
    
    const action_items = [
            {
                label: 'Print Booking',
                icon: <FaPrint className='mr-2 text-dark'/>,
                command: () => {
                    window.open(`/ship-booking/bookings/${booking_id}/print-view`, "_blank", "noopener,noreferrer");
                }
            },
            {
                label: 'Rebook',
                icon: <RiCalendarScheduleFill  className='mr-2 text-maroon' />,
                command: () => {
                    handleChangeStatus(3)
                },
                disabled: !helpers.checkPermissions(['superAdmin', 'shipBookingEdit'], account?.permissions),
                visible: Number(bookingDetails.status_id) < 3 ? true : false
            },
            {
                label: 'Cancel Booking',
                icon: <FaTimesCircle className='mr-2 text-danger'/>,
                command: () => {
                    handleChangeStatus(4)
                },
                disabled: !helpers.checkPermissions(['superAdmin', 'shipBookingEdit'], account?.permissions),
                visible: Number(bookingDetails.status_id) < 3 ? true : false
            },
    ];

    useEffect(() => {
        initTreeView()
        
        if(booking_id){
            fetchData(booking_id)
        }
    }, []);
    
    useEffect(() => {
        const handleResize = () => {
        // 768px is the standard mobile breakpoint (md)
        if (window.innerWidth < 768) {
            setMenuAlignment('left');  // Snaps left edge of menu to left edge of button
        } else {
            setMenuAlignment('right'); // Snaps right edge of menu to right edge of button
        }
        };

        // Run once on load
        handleResize();

        // Listen for screen adjustments
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    function fetchData(id){
        if(!id){
            return
        }
        const url = helpers.createShipBookingHubUrl(`booking/${id}`)

        setLoading(1)
        httpRequest(url, "GET", {}, true)
        .then((res) => {
            setLoading(0)
            setBookingDetails(res)
        })
        .catch((err) => {
            setLoading(0)
            toast.current.show({
                severity: 'error', 
                summary: 'Notice', 
                detail: err.message, 
                life: 5000 
            });
        }); 

    }

    // Formatter for the center display (e.g., 27 Apr 2026)
    const formatDisplayDate = (date) => {
        if (!date) return '';

        // Convert string to Date object if it isn't one already
        const d = typeof date === 'string' ? new Date(date) : date;

        // Check if the date is actually valid to avoid "Invalid Date" strings
        if (isNaN(d.getTime())) return '';

        return d.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTravelDate = (date, type = 'full') => {
        if (!date) return '';

        const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const d = new Date(date);
        const dayName = days[d.getDay()];
        const monthName = months[d.getMonth()];
        const year = d.getFullYear();
        const dateNum = String(d.getDate()).padStart(2, '0');
        const monthNum = String(d.getMonth() + 1).padStart(2, '0');

        if (type === 'short') {
            // Returns: 04 / 27 / 2026
            return `${monthNum} / ${dateNum} / ${year}`;
        }

        // Returns: 2026 Apr 27 (MON)
        return `${year} ${monthName} ${dateNum} (${dayName})`;
    };

    const TripCard = ({ trip, isSelected, onSelect }) => {
        const {
            departure_time,
            estimated_arrival,
            origin,
            destination,
            duration_minutes,
            ship_name,

            //departureCode,
            //arrivalTime,
            //arrivalCode,
            //date,
            //eta,
            //classType,
            //price,
            //vesselCode
        } = trip;

        const convertToDecimalHours = (minutes) => {
            const hours = minutes / 60;
            // .toLocaleString handles the decimal point and rounding
            return `${hours.toLocaleString(undefined, { maximumFractionDigits: 1 })} hr/s`;
        };

        return (
            <div className="position-relative mb-3">
                {/* Red Checkmark Badge for Selected State */}
                {isSelected && (
                    <div className="position-absolute" style={{ top: '-10px', right: '-10px', zIndex: 10 }}>
                        <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center shadow" 
                            style={{ width: '28px', height: '28px', border: '2px solid white' }}>
                            <FaCheck size={12} color="white" />
                        </div>
                    </div>
                )}

                <div className={`card shadow-sm border-0 ${isSelected ? 'selected-trip-card' : ''}`} 
                    style={{ borderRadius: '8px', transition: 'all 0.2s ease' }}>
                    
                    {/* Red left-border indicator for Selected State */}
                    {isSelected && <div className="selected-indicator"></div>}

                    <div className="card-body py-4">

                        <div className="row align-items-center">
                            
                            {/* Departure Info */}
                            <div className="col-md-2">
                                <h5 className="font-weight-bold mb-0">{helpers.formatTime(bookingDetails.departure_time)}</h5>
                                <div className="text-uppercase font-weight-bold small">{origin}</div>
                                <div className="text-muted small">{helpers.formatDateWithWeekDay(bookingDetails.departure_time)}</div>
                            </div>

                            {/* ETA / Route Visual */}
                            <div className="col-md-5 text-center">
                                <div className="text-muted small mb-1">
                                        {ship_name}
                                </div>
                                <div className="d-flex align-items-center justify-content-center">
                                    <FaShip className="text-primary mr-2" />
                                    <div className="flex-grow-1 position-relative" style={{ borderTop: '2px dotted #ccc', minWidth: '100px' }}>
                                        <div className="position-absolute rounded-circle bg-warning" 
                                            style={{ width: '10px', height: '10px', right: '-5px', top: '-6px' }}></div>
                                    </div>
                                </div>
                                <div className="text-muted small mt-1">ETA {convertToDecimalHours(duration_minutes)}</div>
                            </div>

                            {/* Arrival Info */}
                            <div className="col-md-2">
                                <h5 className="font-weight-bold mb-0">{helpers.formatTime(bookingDetails.estimated_arrival)}</h5>
                                <div className="text-uppercase font-weight-bold small">{destination}</div>
                                <div className="text-muted small">{helpers.formatDateWithWeekDay(bookingDetails.estimated_arrival)}</div>
                            </div>

                            {/* Class and Price */}
                            {/* <div className="col-md-3 text-center">
                                <span className="badge badge-pill py-2 px-3 mb-2" 
                                    style={{ backgroundColor: '#fef3e3', color: '#f39c12', fontWeight: '600' }}>
                                    {classType}
                                </span>
                                <h4 className="font-weight-bold mb-0 text-dark">₱{price}</h4>
                            </div> */}

                            {/* Vessel Code */}
                            {/* <div className="col-md-2 text-muted small">
                                {ship_name}
                            </div> */}

                            {/* Action Button */}
                            <div className="col-md-3 text-right">
                                {isSelected ? (
                                    <Button label="Selected" className="p-button-dangers btn btn-primary px-4 rounded" 
                                            // style={{ backgroundColor: '#b71c1c', border: 'none' }}
                                            onClick={() => onSelect(null)} />
                                ) : (
                                    <Button label="Select" className="p-button-outlineds p-button-dangers btn btn-outline-primary px-4 rounded" 
                                            // style={{ borderColor: '#b71c1c', color: '#b71c1c' }}
                                            onClick={() => onSelect(trip.id)} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <style jsx>{`
                    .selected-trip-card {
                        border: 1px solid #007bff !important;
                    }
                    .selected-indicator {
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        width: 5px;
                        background-color: #007bff;
                        border-top-left-radius: 8px;
                        border-bottom-left-radius: 8px;
                    }
                    .p-button:focus {
                        box-shadow: none !important;
                    }
                `}</style>
            </div>
        );
    };

    function handleChangeStatus(status){
        let title = 'Rebook Booking'
        if(status === 4){
            title = 'Cancel Booking'
        }

        Swal.fire({
            title: title,
            text: 'This action cannot be reversed. Do you want to continue?',
            showCancelButton: true,
            showConfirmButton: false,
            closeOnCancel: true,
            showDenyButton: true,
            denyButtonText: 'Yes'
        })
        .then((result) => {
            if (result.isDenied) {
                setLoading(1)
                const url = helpers.createShipBookingHubUrl('booking/status')
                httpRequest(url, 'POST', {
                    postdata: {
                        "ids": [booking_id],
                        "status": status
                    },
                }, true)
                .then((res) => {
                    setLoading(0)
                    
                    toast.current.show({
                        severity: 'success', 
                        summary: 'Redirecting to Rebooking', 
                        detail: res.message, 
                        life: 10000 
                    });

                    if(parseInt(status) === 3){
                        setTimeout(() => {
                        navigate(`/ship-booking/bookings/new?rebook=${booking_id}`);
                        }, 2000); // Matches the 'life' duration of the toast
                    }
                    else{
                        fetchData(booking_id)
                    }

                    //status
                })
                .catch((err) => {
                    console.log(err)
                    setLoading(0)
                    toast.current.show({
                        severity: 'error', 
                        summary: 'Notice', 
                        detail: err.message, 
                        life: 10000 
                    });
                }); 

            }
        })
    }

    function displayScheduleStatusBadge(status_id = '', status_name = ''){
        let label = '';
        let class_name = ''

        switch (Number(status_id)) {
            case 1://Schedule
                class_name = "bg-purple"
                break;
            case 2://Boarding
                class_name = "bg-warning"
                break;
            case 3://Sailed
                class_name = "bg-primary"
                break;
            case 4://Arrived
                class_name = "bg-success"
                break;
            case 5://Cancelled
                class_name = "bg-danger"
                break;
            default:
                class_name = "text-light bg-secondary"
        }

        if(class_name){
            label = <span className={`text-center rounded p-2 text-white text-sm ${class_name}`}>{status_name}</span>
        }

        return label;
    }

    return (
        <>
        <Toast ref={toast} position="top-right" />

        <Layout page_title={page_title} menu={menu} parent_text="Manage Bookings" parent_link='/ship-booking/bookings' breadcrumbs={true}>
            <div className="container-fluid" style={{position: 'relative'}}>
                <Loader isLoading={loading}/>
                <div className="container">
                    <div className='border rounded p-3 px-4 mb-5 d-flex flex-column flex-md-row justify-content-between align-items-center'>
                            <h5 className='mb-3 mb-md-0'>{bookingDetails.booking_ref}</h5>

                            <div>
                                {/* {Number(bookingDetails.status_id) < 4 && (
                                    <Button 
                                        className='btn btn-danger mr-3'
                                        style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                                        onClick={() => handleChangeStatus()}
                                    >Cancel Booking</Button>  
                                )} */}


                                <Button 
                                    className='btn btn-outline-secondary mr-3'
                                    style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                                    onClick={(event) => menuRight.current.toggle(event)} 
                                    aria-controls="popup_menu_right"
                                    aria-haspopup
                                ><BsThreeDotsVertical /></Button> 
                                <Menu 
                                    model={action_items} 
                                    popup ref={menuRight} 
                                    id="popup_menu_right" 
                                    popupAlignment={menuAlignment}
                                />

                                <Link 
                                    className='btn btn-dark' to="/ship-booking/bookings/"
                                    style={{ borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                                >Back</Link>  
                            </div>
                    </div>

                    <div id="review-container" className='row'>
                        <div className='col-lg-8 mb-5 mb-md-0'>
                            <div className='trip-summary mb-5'>
                                <h5 class="font-weight-bold text-uppercase mb-3">Trip Summary</h5> 
                                
                                <div className="departure border-0 rounded bg-white shadow-sm p-5">
                                    {/* <p class="text-muted mb-0">Departure</p> */}

                                    <div className="row align-items-center">
                                        {/* <div className="col-md-6">
                                            <div className='d-flex justify-content-start'>
                                                <h4>{bookingDetails.origin}</h4> 
                                                <span className='text-primary mx-2'>&rarr;</span> 
                                                <h4>
                                                    {bookingDetails.destination}
                                                </h4>
                                            </div>
                                        </div> */}
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Route</p>
                                            <div className='d-flex justify-content-start'>
                                                <h5>{bookingDetails.origin}</h5> 
                                                <span className='text-primary mx-2'>&rarr;</span> 
                                                <h5>
                                                    {bookingDetails.destination}
                                                </h5>
                                            </div>
                                        </div>
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Voyage No</p>
                                            <h5>
                                                {bookingDetails.voyage_no}
                                            </h5>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Departure Date</p>
                                            <h5>
                                                {helpers.formatTravelDate(bookingDetails.departure_time)}
                                            </h5>
                                        </div>
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Travel Time</p>
                                            <h5>
                                                {helpers.formatTime(bookingDetails.departure_time)} - {helpers.formatTime(bookingDetails.estimated_arrival)}
                                            </h5>
                                        </div>
                                    </div>
                                    

                                    <div className="row">
                                        {bookingDetails.trip_type !== 'one-way' && (
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Trip Type</p>
                                            <h5>
                                                {helpers.ucwords(bookingDetails.trip_type)}
                                            </h5>
                                        </div>
                                        )}
                                        <div className="col-lg-6">
                                            <p class="text-muted mb-0">Assigned Ship</p>
                                            <h5>
                                                {bookingDetails.ship_name}
                                            </h5>
                                        </div>
                                        <div className="col-lg-6">
                                            <p class="text-muted mb-1">Schedule Status</p>
                                            <h5>
                                                {displayScheduleStatusBadge(bookingDetails.schedule_status_id, bookingDetails.schedule_status_name)}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                                
                                {bookingDetails.related_trip && (
                                <div className="departure border-0 rounded bg-white shadow-sm p-5 mt-4">

                                    <div className='d-flex justify-content-between align-items-center'>
                                        <h5 class="font-weight-bold mb-0">Related Trip</h5>
                                        {helpers.displayBookingStatusBadge(bookingDetails.related_trip?.status_id, bookingDetails.related_trip?.booking_status)}
                                    </div>

                                    <hr/>

                                    <div className="row align-items-center">
                                        {/* <div className="col-md-6">
                                            <div className='d-flex justify-content-start'>
                                                <h4>{bookingDetails.origin}</h4> 
                                                <span className='text-primary mx-2'>&rarr;</span> 
                                                <h4>
                                                    {bookingDetails.destination}
                                                </h4>
                                            </div>
                                        </div> */}
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Route</p>
                                            <div className='d-flex justify-content-start'>
                                                <h5>{bookingDetails.related_trip?.origin}</h5> 
                                                <span className='text-primary mx-2'>&rarr;</span> 
                                                <h5>
                                                    {bookingDetails.related_trip?.destination}
                                                </h5>
                                            </div>
                                        </div>
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Voyage No</p>
                                            <h5>
                                                {bookingDetails.related_trip?.voyage_no}
                                            </h5>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Departure Date</p>
                                            <h5>
                                                {formatTravelDate(bookingDetails.related_trip?.departure_time)}
                                            </h5>
                                        </div>
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Travel Time</p>
                                            <h5>
                                                {helpers.formatTime(bookingDetails.related_trip?.departure_time)} - {helpers.formatTime(bookingDetails.related_trip?.estimated_arrival)}
                                            </h5>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-lg-6 form-group">
                                            <p class="text-muted mb-0">Trip Type</p>
                                            <h5>
                                                {helpers.ucwords(bookingDetails.related_trip?.trip_type)}
                                            </h5>
                                        </div>
                                        <div className="col-lg-6">
                                            <p class="text-muted mb-0">Booking No</p>
                                            <h5>
                                                <a href={`/ship-booking/bookings/${bookingDetails.related_trip?.id}`}>{bookingDetails.related_trip?.booking_ref}</a>
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                                )}
                            </div>

                            <div className='contact-information mb-5'>
                                <h5 class="font-weight-bold text-uppercase mb-3">Contact Information</h5> 
                                
                                <div className="departure border-0 rounded bg-white shadow-sm p-5">
                                    <div className="row">
                                        <div className="col-lg-4 mb-3 mb-md-0">
                                            <p class="text-muted mb-0">Name</p>
                                            <p className='mb-0'>
                                                {bookingDetails.customer_name}
                                            </p>
                                        </div>
                                        <div className="col-lg-4 mb-3 mb-md-0">
                                            <p class="text-muted mb-0">Phone No.</p>
                                            <p className='mb-0'>
                                                {bookingDetails.customer_phone}
                                            </p>
                                        </div>
                                        <div className="col-lg-4">
                                            <p class="text-muted mb-0">Email</p>
                                            <p className='mb-0'>
                                                {bookingDetails.customer_email}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='contact-information'>
                                <h5 class="font-weight-bold text-uppercase">Vehicle Information</h5> 

                                {bookingDetails?.details?.map((vehicle, index) => {
                                    return (
                                            <div className="departure border-0 rounded bg-white shadow-sm p-5 mt-3">
                                                <div className="row">
                                                    <div className="col-lg-4 mb-3 mb-md-0">
                                                        <p class="text-muted smalls mb-0">Plate Number</p>
                                                        <p className='mb-0'>{vehicle.plate_number}</p>
                                                    </div>
                                                    <div className="col-lg-4 mb-3 mb-md-0">
                                                        <p class="text-muted smalls mb-0">Brand</p>
                                                        <p className='mb-0'>{vehicle.brand}</p>
                                                    </div>
                                                    <div className="col-lg-4 mb-3">
                                                        <p class="text-muted smalls mb-0">Model</p>
                                                        <p className='mb-0'>{vehicle.model_name}</p>
                                                    </div>
                                                    <div className="col-lg-4 mb-3">
                                                        <p class="text-muted smalls mb-0">Driver(s)</p>
                                                        <p className='mb-0'>{vehicle.driver ? vehicle.driver : '---'}</p>
                                                    </div>
                                                    <div className="col-lg-4">
                                                        <p class="text-muted smalls mb-0">Price</p>
                                                        <p className='mb-0'>₱ {helpers.formatAmount(vehicle.unit_price)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                })}
                            </div>
                        </div>

                        <div className='col-lg-4'>
                            <div className='d-flex justify-content-between'>
                                <h5 class="font-weight-bold text-uppercase mb-3">Booking</h5> 
                                <div>{helpers.displayBookingStatusBadge(bookingDetails.status_id, bookingDetails.booking_status)}</div>
                            </div>
                            <div className="departure border-0 rounded bg-white shadow-sm p-5">
                                <div className='mt-0'>
                                    <p class="text-muted mb-0 text-sm">Booking Date</p>
                                    <h5>{formatDisplayDate(bookingDetails.booking_date)}</h5>
                                </div>
                                <div className='mt-3'>
                                    <p class="text-muted mb-0 text-sm">Booking Type</p>
                                    <h5>{bookingDetails.trip_type == 'one-way' ? 'One Way' : 'Round Trip'}</h5>
                                </div>
                                <div className='mt-3'>
                                    <p class="text-muted mb-0 text-sm">Payment Method</p>
                                    <h5>{bookingDetails.payment_name}</h5>
                                </div>
                                
                                <div className='mt-3'>
                                    <p class="text-muted mb-0 text-sm">Total Amount</p>
                                    <h5> ₱ {helpers.formatAmount(bookingDetails.total_amount)}</h5>
                                </div>
                                <div className='mt-3'>
                                    <p class="text-muted mb-0 text-sm">Notes</p>
                                    <div className='text-sm'>{bookingDetails.notes ? bookingDetails.notes : '---'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </Layout>
        </>
    )
}